import { useEffect, useState } from 'react';
import { sheetUrl, byIdUrl } from './config';
import { useTheme } from './theme';
import { useDebug } from './debug';

function nowIsoWithOffset() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  const offMin = -d.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  const abs = Math.abs(offMin);
  const offH = pad(Math.floor(abs / 60));
  const offM = pad(abs % 60);
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}${sign}${offH}:${offM}`;
}

const stylesFor = (theme) => {
  const dark = theme === 'dark';
  return {
    page: {
      padding: 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
      maxWidth: 1000,
    },
    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    h2: { margin: 0, fontSize: 24, color: dark ? '#f8fafc' : '#0f172a' },
    headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
      fontSize: 14,
    },
    btnPrimary: {
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#0ea5e9' : '#0284c7'}`,
      background: '#0284c7',
      color: '#fff',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 700,
      width: '100%',
    },
    btnDanger: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#7f1d1d' : '#ef4444'}`,
      background: '#991b1b',
      color: 'white',
      cursor: 'pointer',
      fontSize: 14,
    },
    toggleWrap: { display: 'flex', alignItems: 'center', gap: 8 },
    toggleLabel: { fontSize: 12, color: dark ? '#cbd5e1' : '#475569' },
    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 1000,
    },
    label: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
      fontWeight: 700,
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? 'rgba(2, 6, 23, 0.55)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
      resize: 'vertical',
    },
    grid2: { display: 'grid', gridTemplateColumns: '1fr', gap: 10 },
    muted: { color: dark ? '#94a3b8' : '#64748b', fontSize: 12 },
    listRow: {
      borderTop: `1px solid ${dark ? 'rgba(148,163,184,0.15)' : '#e5e7eb'}`,
      paddingTop: 12,
      marginTop: 12,
    },
    pill: {
      display: 'inline-block',
      padding: '6px 10px',
      borderRadius: 999,
      border: `1px solid ${dark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.25)'}`,
      background: dark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(248,250,252,0.9)',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 12,
      marginRight: 8,
      marginTop: 6,
      whiteSpace: 'nowrap',
    },
    miniActions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
    divider: {
      borderTop: `1px dashed ${dark ? 'rgba(148,163,184,0.25)' : '#e5e7eb'}`,
      margin: '12px 0',
    },
    stickyBar: {
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
      background: dark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(248, 250, 252, 0.92)',
      borderTop: `1px solid ${dark ? 'rgba(148,163,184,0.18)' : '#e5e7eb'}`,
      padding: 10,
      marginTop: 12,
      borderRadius: 14,
      backdropFilter: 'blur(6px)',
    },
  };
};

export default function AdminMessages() {
  const [theme, setTheme] = useTheme();
  const [debug] = useDebug();
  const S = stylesFor(theme);

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);

  const [id, setId] = useState('');
  const [title, setTitle] = useState('Welcome back!');
  const [body, setBody] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [audience, setAudience] = useState('all');
  const [saveStatus, setSaveStatus] = useState('');

  const [editingId, setEditingId] = useState(null);

  const resetForm = () => {
    setEditingId(null);
    setId('');
    setTitle('Welcome back!');
    setBody('');
    setStartDate('');
    setEndDate('');
    setAudience('all');
    setSaveStatus('');
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(sheetUrl('Messages'), { cache: 'no-store' });
      const data = await res.json();
      const sorted = (data || []).slice().sort((a, b) => {
        const ta = (a.timestamp || '').toString();
        const tb = (b.timestamp || '').toString();
        return tb.localeCompare(ta);
      });
      setMessages(sorted);
    } catch (e) {
      console.error('AdminMessages: fetch failed', e);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  const makeDefaultId = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `msg_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
  };

  const validate = () => {
    if (!(body || '').trim()) return 'Body is required.';
    if (startDate && !/^\d{4}-\d{2}-\d{2}$/.test(startDate))
      return 'Start date must be YYYY-MM-DD or blank.';
    if (endDate && !/^\d{4}-\d{2}-\d{2}$/.test(endDate))
      return 'End date must be YYYY-MM-DD or blank.';
    if (startDate && endDate && endDate < startDate)
      return 'End date must be >= start date.';
    return '';
  };

  const saveMessage = async () => {
    const err = validate();
    if (err) { alert(err); return; }
    setSaveStatus('Saving…');

    const messageId = (id || '').trim() || makeDefaultId();
    const payload = {
      id: messageId,
      title: (title || '').trim() || 'Message',
      body: (body || '').trim(),
      startDate: startDate || '',
      endDate: endDate || '',
      audience: audience || 'all',
      timestamp: nowIsoWithOffset(),
    };

    try {
      if (editingId) {
        const res = await fetch(byIdUrl('Messages', editingId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        });
        if (!res.ok) throw new Error(await res.text());
      } else {
        const res = await fetch(sheetUrl('Messages'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: [payload] }),
        });
        if (!res.ok) throw new Error(await res.text());
      }

      setSaveStatus('Saved.');
      await fetchMessages();
      resetForm();
    } catch (e) {
      console.error('AdminMessages: save failed', e);
      setSaveStatus('Failed.');
      alert('Failed to save message.');
    }
  };

  const editMessage = (m) => {
    setEditingId(m.id);
    setId(m.id || '');
    setTitle(m.title || '');
    setBody(m.body || '');
    setStartDate(m.startDate || '');
    setEndDate(m.endDate || '');
    setAudience(m.audience || 'all');
    setSaveStatus('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteMessage = async (m) => {
    if (!window.confirm(`Delete message "${m.title}" (${m.id})?`)) return;
    try {
      const res = await fetch(byIdUrl('Messages', m.id), { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      await fetchMessages();
    } catch (e) {
      console.error('AdminMessages: delete failed', e);
      alert('Failed to delete message.');
    }
  };

  const audienceLabel = (a) => {
    const x = (a || '').toString().trim().toLowerCase();
    if (!x || x === 'all') return 'Everyone';
    if (x === 'members') return 'Members only';
    if (x === 'board') return 'Board only';
    return a;
  };

  const activeHint = (m) => {
    const s = (m.startDate || '').trim();
    const e = (m.endDate || '').trim();
    if (!s && !e) return 'Always active';
    if (s && e) return `Active: ${s} → ${e}`;
    if (s) return `Active starting: ${s}`;
    return `Active until: ${e}`;
  };

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Admin</div>
          <h2 style={S.h2}>Messages</h2>
          <div style={S.muted}>Create announcements that show in the app.</div>
        </div>

        <div style={S.headerActions}>
          <div style={S.toggleWrap}>
            <span style={S.toggleLabel}>Theme</span>
            <button
              style={S.btn}
              onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
              title="Toggle light/dark"
            >
              {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
            </button>
          </div>
          <button style={S.btn} onClick={fetchMessages}>Refresh</button>
        </div>
      </div>

      {/* Composer */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 800 }}>
            {editingId ? `Editing: ${editingId}` : 'New message'}
          </div>
          {editingId ? (
            <button style={S.btn} onClick={resetForm}>Cancel edit</button>
          ) : null}
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={S.label}>Title</div>
          <input
            style={S.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short headline"
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={S.label}>Body</div>
          <textarea
            style={S.textarea}
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the announcement…"
          />
        </div>

        <div style={S.divider} />

        <div style={S.grid2}>
          <div>
            <div style={S.label}>Start Date (optional)</div>
            <input
              type="date"
              style={S.input}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>End Date (optional)</div>
            <input
              type="date"
              style={S.input}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div>
            <div style={S.label}>Audience</div>
            <select
              style={S.input}
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            >
              <option value="all">Everyone</option>
              <option value="members">Members only</option>
              <option value="board">Board only</option>
            </select>
            <div style={S.muted}>
              “Board only” messages will only display to users with the board role.
            </div>
          </div>

          {debug && (
            <div>
              <div style={S.label}>Message ID (optional)</div>
              <input
                style={S.input}
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="Leave blank to auto-generate"
              />
              <div style={S.muted}>
                If blank, we’ll generate something like <code>msg_YYYYMMDD_HHMM</code>.
              </div>
            </div>
          )}
        </div>

        <div style={S.stickyBar}>
          <button onClick={saveMessage} style={S.btnPrimary}>
            {editingId ? 'Save Changes' : 'Publish Message'}
          </button>
          <div style={{ ...S.muted, marginTop: 6 }}>
            {saveStatus ? `Status: ${saveStatus}` : 'Tip: leave dates blank to show always.'}
          </div>
        </div>
      </div>

      {/* List */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 800 }}>Existing messages</div>
          <div style={S.muted}>
            {loading ? 'Loading…' : `${messages.length} row(s)`}
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: 10, ...S.muted }}>Loading messages…</div>
        ) : messages.length === 0 ? (
          <div style={{ marginTop: 10, ...S.muted }}>No messages yet.</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} style={S.listRow}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{m.title}</div>
              <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{m.body}</div>

              <div>
                <span style={S.pill}>{audienceLabel(m.audience)}</span>
                <span style={S.pill}>{activeHint(m)}</span>
                {m.timestamp ? <span style={S.pill}>{m.timestamp}</span> : null}
                {m.id ? <span style={S.pill}>{m.id}</span> : null}
              </div>

              <div style={S.miniActions}>
                <button style={S.btn} onClick={() => editMessage(m)}>Edit</button>
                <button style={S.btnDanger} onClick={() => deleteMessage(m)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
